import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18n from "@/modules/common/i18n";
import TransRichText from "@/modules/common/i18n/TransRichText";
import type { WorkbenchId } from "@/stores/definitions/container/types";
import ApiService from "../../common/InvApiService";
import { showToastWhilstPending } from "../../util/alerts";
import type { emptyObject } from "../../util/types";
import { mkAlert } from "../contexts/Alert";
import type { GlobalId, Id } from "../definitions/BaseRecord";
import type { Basket, BasketAttrs } from "../definitions/Basket";
import type { Container } from "../definitions/Container";
import type { Factory } from "../definitions/Factory";
import type { Group } from "../definitions/Group";
import type { InventoryRecord } from "../definitions/InventoryRecord";
import type { CoreFetcher, CoreFetcherArgs, ResultType } from "../definitions/Search";
import BasketModel from "../models/Basket";
import ContainerModel, { type ContainerAttrs } from "../models/ContainerModel";
import MemoisedFactory from "../models/Factory/MemoisedFactory";
import InstrumentModel, { type InstrumentAttrs } from "../models/InstrumentModel";
import InstrumentTemplateModel, { type InstrumentTemplateAttrs } from "../models/InstrumentTemplateModel";
import type InventoryBaseRecord from "../models/InventoryBaseRecord";
import SampleModel from "../models/SampleModel";
import Search from "../models/Search";
import TemplateModel, { type TemplateAttrs } from "../models/TemplateModel";
import type { RootStore } from "./RootStore";

export type SavedSearch = CoreFetcherArgs & {
  name: string;
};

export type NewInContainerParams = {
  parentContainers: Array<ContainerModel>;
  /*
   * An empty object is used to denote that a location does not need to be
   * specified when the parent container is a list container
   */
  parentLocation:
    | {
        coordX: number;
        coordY: number;
      }
    | emptyObject;
};

const SAVED_SEARCHES = JSON.parse(localStorage.getItem("searches") ?? "null") as Array<SavedSearch> | null;

/*
 * Over time the format of saved searches has changed and this function
 * ensures that no matter what format the user has saved in their browser,
 * they are made compatible with the latest version.
 */
const normaliseSavedSearches = (searches: Array<SavedSearch>): Array<SavedSearch> => {
  return searches.map((search) => ({
    ...search,
    name: search.name ?? search.query,
  }));
};

export const getContainer = async (id: Id): Promise<ContainerModel> => {
  if (id) {
    const { data } = await ApiService.get<ContainerAttrs>("containers", id);
    return new ContainerModel(new MemoisedFactory(), data);
  }
  throw new Error("Cannot get container without id");
};

/**
 * This class models the state of the main Inventory search, as well as som
 * global state for all searches. It does NOT manage the state of the searches
 * embedded in sample, container, and template forms, nor the move and create
 * dialogs, nor the template picker.
 */
export default class SearchStore {
  rootStore: RootStore;
  search: Search;
  savedSearches: Array<SavedSearch>;
  savedBaskets: Array<BasketModel>;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      search: observable,
      savedSearches: observable,
      savedBaskets: observable,
      saveSearch: action,
      deleteSearch: action,
      getBaskets: action,
      getBasket: action,
      createBasket: action,
      deleteBasket: action,
      createNew: action,
      results: computed,
      fetcher: computed,
      activeResult: computed,
      searchIsSaved: computed,
      activeResultIsBeingEdited: computed,
    });
    this.rootStore = rootStore;
    this.search = new Search({
      factory: new MemoisedFactory(),
      callbacks: {
        setActiveResult: (r: InventoryRecord | null) => r?.refreshAssociatedSearch(),
      },
    });
    this.search.canEditActiveResult = true;
    this.savedSearches = normaliseSavedSearches(SAVED_SEARCHES ?? []);
    this.savedBaskets = [];
  }

  get results(): Array<InventoryRecord> {
    return this.search.filteredResults;
  }

  get fetcher(): CoreFetcher {
    return this.search.fetcher;
  }

  get activeResult(): InventoryRecord | null {
    return this.search.activeResult;
  }

  get searchIsSaved(): boolean {
    return (
      this.savedSearches.findIndex(
        (savedSearch) => JSON.stringify(savedSearch) === JSON.stringify(this.search.fetcher.serialize),
      ) !== -1
    );
  }

  /*
   * Save the current search parameters as a saved search, or rename an
   * existing saved search. Saved searches are stored in the browser's
   * localStorage so this method has the side effect of mutating that state.
   *
   * @param name The name for the new search/new name for the existing saved
   *             search
   * @param index If null then a new search is created with the current search
   *              parameters. If a valid index of the `savedSearches` array
   *              then the saved search at that index is renamed. Any other
   *              value will cause an error.
   */
  saveSearch(name: string, index?: number): void {
    if (this.savedSearches.map((s) => s.name).includes(name)) throw new Error(`"${name}" already exists`);
    if (typeof index === "number") {
      const updatedSearch = { ...this.savedSearches[index], name };
      this.savedSearches.splice(index, 1, updatedSearch);
      localStorage.setItem("searches", JSON.stringify(this.savedSearches));
    } else {
      const newSearch = {
        ...this.search.fetcher.serialize,
        name,
      };
      this.savedSearches.push(newSearch);
      localStorage.setItem("searches", JSON.stringify(this.savedSearches));
    }
  }

  deleteSearch(search: SavedSearch) {
    this.savedSearches = this.savedSearches.filter(
      (savedSearch) => JSON.stringify(savedSearch) !== JSON.stringify(search),
    );
    localStorage.setItem("searches", JSON.stringify(this.savedSearches));
  }

  async getBaskets(): Promise<void> {
    const { uiStore } = this.rootStore;
    try {
      const { data } = await ApiService.get<Array<BasketAttrs>>("baskets");
      runInAction(() => {
        this.savedBaskets = data.map((basketAttrs) => new BasketModel(basketAttrs));
      });
    } catch (e) {
      uiStore.addAlert(
        mkAlert({
          title: i18n.t("inventory:baskets.alerts.fetchFailed"),
          message: (e as Error).message || "",
          variant: "error",
          isInfinite: true,
        }),
      );
    }
  }

  async getBasket(id: Id): Promise<Basket | undefined> {
    const { uiStore } = this.rootStore;
    try {
      if (id) {
        const { data } = await ApiService.get<BasketAttrs>(`baskets/${id}`);
        if (data) return new BasketModel(data);
      } else throw new Error("Cannot retrieve Basket without id");
    } catch (e) {
      uiStore.addAlert(
        mkAlert({
          title: i18n.t("inventory:baskets.alerts.fetchOneFailed"),
          message: (e as Error).message || "",
          variant: "error",
          isInfinite: true,
        }),
      );
    }
  }

  async createBasket(name?: string, items?: Array<GlobalId>): Promise<void> {
    const { uiStore } = this.rootStore;
    try {
      const res = await showToastWhilstPending(
        i18n.t("inventory:baskets.pending.creating"),
        ApiService.post<BasketAttrs>("baskets", { name, globalIds: items }),
      );
      if (res.status === 201 && res.data) {
        // refetch to update list
        await this.getBaskets();
        uiStore.addAlert(
          mkAlert({
            title: i18n.t("inventory:baskets.alerts.created", { name: res.data.name }),
            message: items ? i18n.t("inventory:baskets.alerts.selectedItemsAdded") : "",
            variant: "success",
            isInfinite: false,
          }),
        );
      }
    } catch (e) {
      uiStore.addAlert(
        mkAlert({
          title: i18n.t("inventory:baskets.alerts.createFailed"),
          message: (e as Error).message || "",
          variant: "error",
          isInfinite: true,
        }),
      );
    }
  }

  async deleteBasket(id: Id): Promise<void> {
    const { uiStore } = this.rootStore;
    try {
      if (id) {
        const name = this.savedBaskets.find((b) => b.id === id)?.name ?? i18n.t("inventory:baskets.fallbackName");
        if (
          await uiStore.confirm(
            i18n.t("inventory:baskets.delete.title"),
            <TransRichText i18nKey="inventory:baskets.delete.body" values={{ name }} />,
            i18n.t("common:actions.ok"),
            i18n.t("common:actions.cancel"),
          )
        ) {
          const res = await showToastWhilstPending(
            i18n.t("inventory:baskets.pending.deleting"),
            ApiService.delete<void>(`baskets`, id),
          );
          if (res.status === 200) {
            // refetch to update list
            await this.getBaskets();
            uiStore.addAlert(
              mkAlert({
                title: i18n.t("inventory:baskets.alerts.deleted", { name }),
                message: i18n.t("inventory:baskets.alerts.contentsUnaffected"),
                variant: "success",
                isInfinite: false,
              }),
            );
          }
        }
      } else {
        throw new Error("Cannot delete Basket without id");
      }
    } catch (e) {
      uiStore.addAlert(
        mkAlert({
          title: i18n.t("inventory:baskets.alerts.deleteFailed"),
          message: (e as Error).message || "",
          variant: "error",
          isInfinite: true,
        }),
      );
    }
  }

  async createNewHelper(newRecord: InventoryBaseRecord): Promise<void> {
    await this.search.setActiveResult(newRecord);
    this.rootStore.uiStore.setVisiblePanel("right");
  }

  async createNewSample(subsampleParentDetails?: NewInContainerParams): Promise<SampleModel> {
    const currentUsersGroups = await this.rootStore.peopleStore.fetchCurrentUsersGroups();
    const sample = new SampleModel(new MemoisedFactory());
    sample.setAttributes({
      ...(subsampleParentDetails
        ? {
            newSampleSubSampleTargetLocations: [
              {
                containerId: subsampleParentDetails.parentContainers[0].id,
                location: subsampleParentDetails.parentLocation,
              },
            ],
          }
        : {}),
      sharedWith: currentUsersGroups.map((group) => ({
        group,
        shared: true,
        itemOwnerGroup: true,
      })),
    });
    await this.createNewHelper(sample);
    return sample;
  }

  async createNewContainer(containerParentDetails?: NewInContainerParams): Promise<ContainerModel> {
    const [currentUsersBench, currentUsersGroups] = await Promise.all<
      [Promise<Container> | null, Promise<Array<Group>>]
    >([
      this.rootStore.peopleStore.currentUser === null ? null : this.rootStore.peopleStore.currentUser.getBench(),
      this.rootStore.peopleStore.fetchCurrentUsersGroups(),
    ]);
    const container = new ContainerModel(new MemoisedFactory());
    const locationIsDefined: boolean | undefined =
      containerParentDetails && Object.keys(containerParentDetails.parentLocation).length > 0;

    container.setAttributes({
      parentContainers: containerParentDetails?.parentContainers ?? [currentUsersBench],
      ...(containerParentDetails && locationIsDefined ? { parentLocation: containerParentDetails.parentLocation } : {}),
      sharedWith: currentUsersGroups.map((group) => ({
        group,
        shared: true,
        itemOwnerGroup: true,
      })),
    });
    await this.createNewHelper(container);
    return container;
  }

  async createNewTemplate(): Promise<TemplateModel> {
    const currentUsersGroups = await this.rootStore.peopleStore.fetchCurrentUsersGroups();
    const template = new TemplateModel(new MemoisedFactory());
    template.setAttributes({
      sharedWith: currentUsersGroups.map((group) => ({
        group,
        shared: true,
        itemOwnerGroup: true,
      })),
    });
    await this.createNewHelper(template);
    return template;
  }

  async createNewInstrument(
    opts?: { templateId?: Id | null },
    parentContainerDetails?: NewInContainerParams,
  ): Promise<InstrumentModel> {
    const template =
      opts?.templateId &&
      this.activeResult instanceof InstrumentTemplateModel &&
      this.activeResult.id === opts.templateId
        ? this.activeResult
        : null;

    const currentUsersGroups = await this.rootStore.peopleStore.fetchCurrentUsersGroups();

    const instrumentAttrs: InstrumentAttrs = {
      id: null,
      type: "INSTRUMENT",
      globalId: null,
      name: "",
      permittedActions: ["READ", "UPDATE", "CHANGE_OWNER"],
      tags: null,
      owner: null,
      created: null,
      deleted: false,
      lastModified: null,
      modifiedByFullName: null,
      attachments: [],
      barcodes: [],
      identifiers: [],
      fields: [],
      parentContainers: [],
      parentLocation: null,
      lastNonWorkbenchParent: null,
      lastMoveDate: null,
      _links: [],
    };
    const instrument = new InstrumentModel(new MemoisedFactory(), instrumentAttrs);
    instrument.setAttributes({
      sharedWith: currentUsersGroups.map((group) => ({
        group,
        shared: true,
        itemOwnerGroup: true,
      })),
    });
    if (template) {
      await instrument.setTemplate(template);
    }
    const locationIsDefined = parentContainerDetails
      ? Object.keys(parentContainerDetails.parentLocation).length > 0
      : false;
    if (parentContainerDetails) {
      instrument.setAttributes({
        parentContainers: parentContainerDetails.parentContainers,
        ...(locationIsDefined ? { parentLocation: parentContainerDetails.parentLocation } : {}),
      });
    }
    await this.createNewHelper(instrument);
    return instrument;
  }

  async createNewInstrumentTemplate(): Promise<InstrumentTemplateModel> {
    const currentUsersGroups = await this.rootStore.peopleStore.fetchCurrentUsersGroups();
    const instrumentTemplate = new InstrumentTemplateModel(new MemoisedFactory(), {
      id: null,
      type: "INSTRUMENT_TEMPLATE",
      globalId: null,
      name: "",
      permittedActions: ["READ", "UPDATE", "CHANGE_OWNER"],
      tags: null,
      owner: null,
      created: null,
      deleted: false,
      lastModified: null,
      modifiedByFullName: null,
      attachments: [],
      barcodes: [],
      identifiers: [],
      sharingMode: "OWNER_GROUPS",
      _links: [],
    });
    instrumentTemplate.setAttributes({
      sharedWith: currentUsersGroups.map((group) => ({
        group,
        shared: true,
        itemOwnerGroup: true,
      })),
    });
    await this.createNewHelper(instrumentTemplate);
    return instrumentTemplate;
  }

  createNew(
    type: "sample" | "container" | "template" | "instrument" | "instrumentTemplate",
  ): Promise<InventoryBaseRecord> {
    if (type === "sample") return this.createNewSample();
    if (type === "container") return this.createNewContainer();
    if (type === "instrument") return this.createNewInstrument();
    if (type === "instrumentTemplate") return this.createNewInstrumentTemplate();
    return this.createNewTemplate();
  }

  async getBench(workbenchId: WorkbenchId): Promise<Container> {
    const { data } = await ApiService.get<ContainerAttrs>("workbenches", workbenchId);
    return new ContainerModel(new MemoisedFactory(), data);
  }

  async getTemplate(id: number, version: number | null | undefined, factory: Factory): Promise<TemplateModel> {
    const { data } = await ApiService.get<TemplateAttrs>(
      "sampleTemplates",
      typeof version === "number" ? `${id}/versions/${version}` : `${id}`,
    );
    return new TemplateModel(factory, data);
  }

  async getInstrumentTemplate(
    id: number,
    version: number | null | undefined,
    factory: Factory,
  ): Promise<InstrumentTemplateModel> {
    const { data } = await ApiService.get<InstrumentTemplateAttrs>(
      "instrumentTemplates",
      typeof version === "number" ? `${id}/versions/${version}` : `${id}`,
    );
    return new InstrumentTemplateModel(factory, data);
  }

  get activeResultIsBeingEdited(): boolean {
    return this.activeResult?.editing ?? false;
  }

  isTypeSelected(resultType: ResultType): boolean {
    return !this.search.fetcher.parentGlobalId && this.search.fetcher.resultType === resultType;
  }
}
